// src/components/dashboard/WeatherCard.js
import React from "react";
import { Typography, Box, useMediaQuery } from "@mui/material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import CloudIcon from "@mui/icons-material/Cloud";
import AllDivesiteDetailsWeather from "../weather/alldivesiteweather.js";

// Glassmorphic Weather Card
const GlassWeatherCard = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.85)} 0%, 
    ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 16,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.06)}`,
  padding: theme.spacing(2.5),
  display: "inline-block",
  width: "auto",
  minWidth: 280,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.info.main, 0.4)}, 
      ${alpha(theme.palette.primary.main, 0.3)}, 
      transparent)`,
    borderRadius: "16px 16px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 25% 25%, 
      ${alpha(theme.palette.info.main, 0.03)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 75% 75%, 
      ${alpha(theme.palette.primary.main, 0.02)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },

  // Mobile responsive
  [theme.breakpoints.down("md")]: {
    borderRadius: 12,
    padding: theme.spacing(2),
    minWidth: 260,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 12px 40px ${alpha(theme.palette.info.main, 0.08)}`,
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.9)} 0%, 
        ${alpha(theme.palette.background.paper, 0.75)} 100%)`,
      borderColor: alpha(theme.palette.info.main, 0.18),
    },
  },

  // Desktop hover effects
  [theme.breakpoints.up("md")]: {
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: `0 16px 48px ${alpha(theme.palette.info.main, 0.1)}`,
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.9)} 0%, 
        ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
      borderColor: alpha(theme.palette.info.main, 0.2),
      "&::before": {
        background: `linear-gradient(90deg, 
          transparent, 
          ${alpha(theme.palette.info.main, 0.5)}, 
          ${alpha(theme.palette.primary.main, 0.4)}, 
          transparent)`,
      },
      "&::after": {
        background: `radial-gradient(circle at 25% 25%, 
          ${alpha(theme.palette.info.main, 0.04)} 0%, 
          transparent 50%), 
          radial-gradient(circle at 75% 75%, 
          ${alpha(theme.palette.primary.main, 0.03)} 0%, 
          transparent 50%)`,
      },
    },
  },

  [theme.breakpoints.down("sm")]: {
    borderRadius: 10,
    padding: theme.spacing(1.5),
    minWidth: 240,
  },
}));

// Glassmorphic Header Box
const GlassHeaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 1.5),
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.info.main, 0.08)} 0%, 
    ${alpha(theme.palette.info.main, 0.04)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
  borderRadius: 10,
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.info.light, 0.3)}, 
      transparent)`,
    borderRadius: "10px 10px 0 0",
  },

  [theme.breakpoints.down("sm")]: {
    borderRadius: 8,
    padding: theme.spacing(0.75, 1),
    gap: theme.spacing(0.75),
  },
}));

// Glassmorphic Weather Icon
const GlassWeatherIcon = styled(CloudIcon)(({ theme }) => ({
  color: theme.palette.info.main,
  filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.info.main, 0.3)})`,
  fontSize: "1.5rem",

  [theme.breakpoints.down("sm")]: {
    fontSize: "1.25rem",
  },
}));

const WeatherCard = ({ divesite }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <GlassWeatherCard>
      <GlassHeaderBox>
        <GlassWeatherIcon />
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          fontWeight="bold"
          color="primary"
          sx={{
            fontSize: isMobile ? "1rem" : isTablet ? "1.1rem" : "1.25rem",
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {divesite.uddf.divesite.site.name}
        </Typography>
      </GlassHeaderBox>

      <Box sx={{ position: "relative", zIndex: 2 }}>
        <AllDivesiteDetailsWeather divesiteId={divesite._id} compact />
      </Box>
    </GlassWeatherCard>
  );
};

export default WeatherCard;
